import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import ISpecie from '../../interfaces/ISpecie'
import { SearchRequest, SpecieSaveRequest, SpecieDeleteRequest } from '../../services/api/interfaces/ApiRequest'
import { specieList, specieCreate, specieUpdate, specieDelete } from '../../services/api'

interface SpecieContextState extends BaseContextState {
  species: Array<ISpecie> | null,
  specie: ISpecie | null
}

export interface SpecieContextData extends SpecieContextState {
  resetErrors: Function,
  requestSpecieList: (args: SearchRequest) => Promise<any>,
  requestSpecieFind: (args: SearchRequest) => void,
  requestSpecieCreate: (args: SpecieSaveRequest) => Promise<any>,
  requestSpecieUpdate: (args: SpecieSaveRequest) => Promise<any>,
  requestSpecieDelete: (args: SpecieDeleteRequest) => Promise<any>
}

// Create context
const { Provider, Consumer } = createContext({} as SpecieContextData)

// Export consumer
export const SpecieConsumer = Consumer

export class SpecieProvider extends BaseContextProvider {
  state: SpecieContextState = {
    species: null,
    specie: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for specie list
   * 
   * @param {SearchRequest} payload 
   */
  requestSpecieList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        this.lastListPayload = payload
        const response = await specieList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'species'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for specie finder
   * 
   * @param {SearchRequest} payload 
   */
  requestSpecieFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      specie: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await specieList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'specie'
        }
      })
    })
  }

  /**
   * Make api request for specie create
   * 
   * @param {SpecieSaveRequest} payload 
   */
  requestSpecieCreate = (payload: SpecieSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        specie: null,
      }, async () => {
        let response = await specieCreate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'specie', destination: 'specie' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for specie update
   * 
   * @param {SpecieSaveRequest} payload 
   */
  requestSpecieUpdate = (payload: SpecieSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await specieUpdate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'specie', destination: 'specie' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for specie delete
   * 
   * @param {SpecieDeleteRequest} payload 
   */
  requestSpecieDelete = (payload: SpecieDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await specieDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestSpecieList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestSpecieList: this.requestSpecieList,
          requestSpecieFind: this.requestSpecieFind,
          requestSpecieCreate: this.requestSpecieCreate,
          requestSpecieUpdate: this.requestSpecieUpdate,
          requestSpecieDelete: this.requestSpecieDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
